import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Grid,
    TextField,
    Typography,
    Box,
    Card,
    CardContent,
    Stack,
    Alert,
    LinearProgress,
    Button,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Divider,
    useTheme,
    alpha
} from '@mui/material';
import {
    Analytics as AnalyticsIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Remove as RemoveIcon,
    Add as AddIcon,
    Calculate as CalculateIcon,
    Assessment as AssessmentIcon,
    Info as InfoIcon,
    FilterList as FilterListIcon
} from '@mui/icons-material';
import { calculateDeviation } from './utils';

// Test types data defined directly here
const testTypes = [
    { value: 'pH', label: 'pH Level', unit: 'pH', category: 'Chemical' },
    { value: 'viscosity', label: 'Viscosity', unit: 'cP', category: 'Physical' },
    { value: 'density', label: 'Density/Specific Gravity', unit: 'g/ml', category: 'Physical' },
    { value: 'temperature', label: 'Temperature Control', unit: '°C', category: 'Physical' },
    { value: 'homogeneity', label: 'Homogeneity/Mixing Uniformity', unit: 'pass/fail', category: 'Physical' },
    { value: 'color', label: 'Color Consistency', unit: 'ΔE', category: 'Sensory' },
    { value: 'odor', label: 'Odor Profile', unit: 'scale', category: 'Sensory' },
    { value: 'appearance', label: 'Visual Inspection', unit: 'pass/fail', category: 'Sensory' },
    { value: 'moisture', label: 'Water Content', unit: '%', category: 'Chemical' },
    { value: 'particle_size', label: 'Particle Size Distribution', unit: 'μm', category: 'Physical' },
    { value: 'emulsion_stability', label: 'Emulsion Stability', unit: 'min', category: 'Physical' },
    { value: 'conductivity', label: 'Electrical Conductivity', unit: 'μS/cm', category: 'Chemical' }
];

export default function ResultsStep({ 
    formData, 
    setFormData, 
    onValidationChange,
    showSnackbar,
    editingTest,
    onSaveTest
}) {
    const theme = useTheme();
    const [validationErrors, setValidationErrors] = useState({});
    const [resultsComplete, setResultsComplete] = useState(false);
    const [resultAnalysis, setResultAnalysis] = useState({});
    const [multipleResults, setMultipleResults] = useState([]);
    const [statisticalSummary, setStatisticalSummary] = useState({});
    const [showCalculationDetails, setShowCalculationDetails] = useState(false);

    // All tests are now pass/fail only
    const specificationType = 'pass_fail';

    // Simple validation - ONLY require PASS/FAIL result
    const validateResults = () => {
        const errors = {};
        
        // ONLY validate that PASS or FAIL is selected - NOTHING ELSE MATTERS
        if (!formData.result_text || (formData.result_text !== 'PASS' && formData.result_text !== 'FAIL')) {
            errors.result_text = 'Result must be either PASS or FAIL';
        }

        setValidationErrors(errors);
        
        // Results are complete if PASS or FAIL is selected - PERIOD
        const resultsReady = !!(formData.result_text && (formData.result_text === 'PASS' || formData.result_text === 'FAIL'));
        
        setResultsComplete(resultsReady);
        
        // Always return true if PASS/FAIL is selected, regardless of other fields
        return resultsReady;
    };

    // Calculate comprehensive result analysis
    const calculateResultAnalysis = useCallback(() => {
        let analysis = {};
        
        if (formData.actual_result && formData.target_value) {
            const actual = parseFloat(formData.actual_result);
            const target = parseFloat(formData.target_value);
            const min = parseFloat(formData.min_acceptable);
            const max = parseFloat(formData.max_acceptable);
            
            analysis = {
                actual: actual,
                target: target,
                deviation: calculateDeviation(actual, target),
                absoluteDeviation: Math.abs(actual - target),
                relativeDeviation: ((actual - target) / target) * 100,
                withinSpec: actual >= min && actual <= max,
                distanceFromMin: actual - min,
                distanceFromMax: max - actual,
                capacityIndex: calculateCapacityIndex(actual, target, min, max),
                result_status: determineResultStatus(actual, target, min, max)
            };
        }

        // Calculate statistical summary for multiple results
        if (multipleResults.length > 1) {
            const values = multipleResults.map(r => parseFloat(r.value)).filter(v => !isNaN(v));
            if (values.length > 1) {
                const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
                const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
                const stdDev = Math.sqrt(variance);
                const min = Math.min(...values);
                const max = Math.max(...values);
                const range = max - min;
                
                setStatisticalSummary({
                    count: values.length,
                    mean: mean.toFixed(3),
                    stdDev: stdDev.toFixed(3),
                    cv: ((stdDev / mean) * 100).toFixed(2),
                    min: min.toFixed(3),
                    max: max.toFixed(3),
                    range: range.toFixed(3),
                    median: calculateMedian(values).toFixed(3)
                });
            }
        }
        
        setResultAnalysis(analysis);
    }, [formData.actual_result, formData.target_value, formData.min_acceptable, formData.max_acceptable, multipleResults]);

    const calculateCapacityIndex = (actual, target, min, max) => {
        if (isNaN(actual) || isNaN(target) || isNaN(min) || isNaN(max)) return null;
        
        const upperLimit = max - target;
        const lowerLimit = target - min;
        const actualDeviation = Math.abs(actual - target);
        
        if (upperLimit <= 0 || lowerLimit <= 0) return null;
        
        const cpk = Math.min(
            (max - actual) / (3 * (upperLimit / 3)),
            (actual - min) / (3 * (lowerLimit / 3))
        );
        
        return cpk.toFixed(3);
    };

    const calculateMedian = (values) => {
        const sorted = [...values].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 === 0 
            ? (sorted[mid - 1] + sorted[mid]) / 2 
            : sorted[mid];
    };

    const determineResultStatus = (actual, target, min, max) => {
        if (actual < min || actual > max) return 'OUT OF SPEC';
        
        const deviation = Math.abs(actual - target);
        const tolerance = Math.min(target - min, max - target);
        const deviationPercent = (deviation / tolerance) * 100;
        
        if (deviationPercent <= 25) return 'EXCELLENT';
        if (deviationPercent <= 50) return 'GOOD';
        if (deviationPercent <= 75) return 'ACCEPTABLE';
        return 'MARGINAL';
    };

    // Notify parent of validation status when data changes
    useEffect(() => {
        const timer = setTimeout(() => {
            // AGGRESSIVE BYPASS - if PASS/FAIL is selected, ALWAYS return valid
            const hasPassFail = formData.result_text === 'PASS' || formData.result_text === 'FAIL';
            
            if (hasPassFail) {
                setValidationErrors({});
                setResultsComplete(true);
                if (onValidationChange) {
                    onValidationChange('results', true);
                }
            } else {
                const isValid = validateResults();
                calculateResultAnalysis();
                if (onValidationChange) {
                    onValidationChange('results', isValid);
                }
            }
        }, 200);
        return () => clearTimeout(timer);
    }, [
        formData.result_text,
        formData.actual_result,
        formData.target_value,
        formData.min_acceptable,
        formData.max_acceptable,
        multipleResults
    ]);

    const handleActualValueChange = (value) => {
        setFormData(prev => ({...prev, actual_result: value}));
    };

    const handleUncertaintyChange = (value) => {
        setFormData(prev => ({...prev, measurement_uncertainty: value}));
    };

    const handleResultTypeChange = (value) => {
        setFormData(prev => ({...prev, result_type: value}));
        if (value === 'single') {
            setMultipleResults([]);
        } else if (value === 'average' && multipleResults.length === 0) {
            setMultipleResults([{ id: 1, value: '', notes: '' }]);
        }
    };

    const addResult = () => {
        const newResult = {
            id: Date.now(),
            value: '',
            notes: ''
        };
        setMultipleResults([...multipleResults, newResult]);
    };

    const removeResult = (id) => {
        setMultipleResults(multipleResults.filter(r => r.id !== id));
    };

    const updateResult = (id, field, value) => {
        setMultipleResults(multipleResults.map(r => 
            r.id === id ? { ...r, [field]: value } : r
        ));
    };

    const calculateAverageFromMultiple = () => {
        const values = multipleResults.map(r => parseFloat(r.value)).filter(v => !isNaN(v));
        if (values.length > 0) {
            const average = values.reduce((sum, val) => sum + val, 0) / values.length;
            handleActualValueChange(average.toFixed(3));
            showSnackbar?.(`Calculated average: ${average.toFixed(3)} from ${values.length} results`, 'success');
        }
    };

    const getResultStatusColor = (status) => {
        switch (status) {
            case 'EXCELLENT': return '#4caf50';
            case 'GOOD': return '#8bc34a';
            case 'ACCEPTABLE': return '#ff9800';
            case 'MARGINAL': return '#ff5722';
            case 'OUT OF SPEC': return '#f44336';
            default: return '#999';
        }
    };

    const getResultStatusIcon = (status) => {
        switch (status) {
            case 'EXCELLENT':
            case 'GOOD':
                return <CheckCircleIcon sx={{ color: getResultStatusColor(status) }} />;
            case 'ACCEPTABLE':
                return <WarningIcon sx={{ color: getResultStatusColor(status) }} />;
            case 'MARGINAL':
            case 'OUT OF SPEC':
                return <ErrorIcon sx={{ color: getResultStatusColor(status) }} />;
            default:
                return <InfoIcon sx={{ color: getResultStatusColor(status) }} />;
        }
    };

    // Get results completion percentage
    const getCompletionPercentage = () => {
        const requiredFields = ['actual_result'];
        const completedFields = requiredFields.filter(field => 
            formData[field] || multipleResults.length > 0
        );
        return (completedFields.length / requiredFields.length) * 100;
    };

    const completionPercentage = getCompletionPercentage();



    return (
        <Box sx={{ p: 4 }}>
            {/* Header with Progress */}
            <Card sx={{ 
                borderRadius: 4,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                backdropFilter: 'blur(20px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                mb: 4
            }}>
                <CardContent sx={{ p: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                        <Box sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            backdropFilter: 'blur(10px)',
                            mr: 1
                        }}>
                            <AnalyticsIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                        </Box>
                        <Box flex={1}>
                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                Results & Analysis
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Record test results, perform calculations, and analyze data against specifications
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h5" sx={{ fontWeight: 500, color: resultsComplete ? theme.palette.success.main : theme.palette.warning.main }}>
                                {Math.round(completionPercentage)}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Complete
                            </Typography>
                        </Box>
                    </Stack>
                    
                    <LinearProgress 
                        variant="determinate" 
                        value={completionPercentage} 
                        sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.grey[400], 0.3),
                            '& .MuiLinearProgress-bar': {
                                background: resultsComplete 
                                    ? `linear-gradient(90deg, ${theme.palette.success.main}, ${alpha(theme.palette.success.main, 0.8)})`
                                    : `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.8)})`
                            }
                        }} 
                    />
                </CardContent>
            </Card>
            
            <Grid container spacing={4}>

            {/* Specification Context */}
            {(formData.target_value || formData.min_acceptable || formData.max_acceptable) && (
                <Grid item xs={12}>
                    <Card sx={{ 
                        borderRadius: 4,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <AssessmentIcon sx={{ color: theme.palette.primary.main }} />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                        Specification Range
                                    </Typography>
                                    <Stack direction="row" spacing={3} sx={{ mt: 0.5 }}>
                                        {formData.target_value && (
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                <strong>Target:</strong> {formData.target_value} {formData.target_unit}
                                            </Typography>
                                        )}
                                        {formData.min_acceptable && formData.max_acceptable && (
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                <strong>Range:</strong> {formData.min_acceptable} - {formData.max_acceptable} {formData.target_unit}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            )}

            {/* Validation Alerts */}
            {Object.keys(validationErrors).length > 0 && (
                <Grid item xs={12}>
                    <Alert 
                        severity="error"
                        sx={{ 
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                            '& .MuiAlert-icon': {
                                color: theme.palette.error.main
                            }
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ 
                            fontWeight: 500, 
                            mb: 1, 
                            color: theme.palette.error.main
                        }}>
                            Result Issues:
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            {Object.values(validationErrors).map((error, index) => (
                                <li key={index}>
                                    <Typography variant="body2" sx={{ 
                                        color: theme.palette.error.main,
                                        fontWeight: 500 
                                    }}>{error}</Typography>
                                </li>
                            ))}
                        </ul>
                    </Alert>
                </Grid>
            )}

            {/* Result Type Selection */}
            <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                    <InputLabel sx={{ fontWeight: 500 }}>Result Type</InputLabel>
                    <Select
                        value={formData.result_type || 'single'}
                        label="Result Type"
                        onChange={(e) => handleResultTypeChange(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '& .MuiSelect-select': {
                                py: 2,
                                fontWeight: 500
                            },
                            backgroundColor: 'white',
                            borderRadius: 3,
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }}
                    >
                        <MenuItem value="single" sx={{ py: 2 }}>
                            <Box>
                                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem' }}>Single Result</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                    One measurement value
                                </Typography>
                            </Box>
                        </MenuItem>
                        <MenuItem value="average" sx={{ py: 2 }}>
                            <Box>
                                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem' }}>Multiple Results (Average)</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                    Multiple measurements with statistical analysis
                                </Typography>
                            </Box>
                        </MenuItem>

                    </Select>
                </FormControl>
            </Grid>

            {/* Pass/Fail Result Selection - Simple */}
            <Grid item xs={12} md={4}>
                <FormControl fullWidth required error={!!validationErrors.result_text}>
                    <InputLabel sx={{ fontWeight: 500 }}>Test Result *</InputLabel>
                    <Select
                        value={formData.result_text || ''}
                        label="Test Result *"
                        onChange={(e) => setFormData(prev => ({...prev, result_text: e.target.value}))}
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '& .MuiSelect-select': {
                                py: 2,
                                fontWeight: 500
                            },
                            backgroundColor: 'white',
                            borderRadius: 3,
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }}
                    >
                        <MenuItem value="PASS" sx={{ py: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                                <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.success.main }}>
                                    PASS
                                </Typography>
                            </Box>
                        </MenuItem>
                        <MenuItem value="FAIL" sx={{ py: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ErrorIcon sx={{ color: theme.palette.error.main }} />
                                <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.error.main }}>
                                    FAIL
                                </Typography>
                            </Box>
                        </MenuItem>
                    </Select>
                    {validationErrors.result_text && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                            {validationErrors.result_text}
                        </Typography>
                    )}
                </FormControl>
            </Grid>
                
            <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                    label="Measurement Uncertainty"
                    type="number"
                    value={formData.measurement_uncertainty || ''}
                    onChange={(e) => handleUncertaintyChange(e.target.value)}
                    InputProps={{
                        endAdornment: <InputAdornment position="end">±{formData.target_unit}</InputAdornment>
                    }}
                    InputLabelProps={{
                        sx: { fontWeight: 500 }
                    }}
                    helperText="Optional: measurement uncertainty/error margin"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3
                            },
                            '&:hover fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '&.Mui-focused fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '& input': {
                                py: 2,
                                fontWeight: 500
                            },
                            backgroundColor: 'white',
                            borderRadius: 3,
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }
                    }}
                    />
                </Grid>
                
            {/* Multiple Results Section */}
            {formData.result_type === 'average' && (
                <Grid item xs={12}>
                    <Card sx={{ 
                        borderRadius: 4,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                    Multiple Measurements
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={addResult}
                                        size="small"
                                        variant="outlined"
                                    >
                                        Add Result
                                    </Button>
                                    {multipleResults.length > 0 && (
                                        <Button
                                            startIcon={<CalculateIcon />}
                                            onClick={calculateAverageFromMultiple}
                                            size="small"
                                            variant="contained"
                                        >
                                            Calculate Average
                                        </Button>
                                    )}
                                </Stack>
                            </Stack>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Measurement #</TableCell>
                                            <TableCell>Value ({formData.target_unit})</TableCell>
                                            <TableCell>Notes</TableCell>
                                            <TableCell width={50}></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {multipleResults.map((result, index) => (
                                            <TableRow key={result.id}>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={result.value}
                                                        onChange={(e) => updateResult(result.id, 'value', e.target.value)}
                                                        error={!!validationErrors[`result_${index}`]}
                                                        fullWidth
                                                    />
                                                </TableCell>
                                                <TableCell>
                    <TextField
                                                        size="small"
                                                        value={result.notes}
                                                        onChange={(e) => updateResult(result.id, 'notes', e.target.value)}
                                                        placeholder="Optional notes"
                        fullWidth
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeResult(result.id)}
                                                        color="error"
                                                        disabled={multipleResults.length <= 1}
                                                    >
                                                        <RemoveIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Statistical Summary */}
                            {Object.keys(statisticalSummary).length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                                        Statistical Summary
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6} md={3}>
                                            <Paper sx={{ p: 1, textAlign: 'center' }}>
                                                <Typography variant="caption" color="text.secondary">Mean</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                                    {statisticalSummary.mean}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Paper sx={{ p: 1, textAlign: 'center' }}>
                                                <Typography variant="caption" color="text.secondary">Std Dev</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                                    {statisticalSummary.stdDev}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Paper sx={{ p: 1, textAlign: 'center' }}>
                                                <Typography variant="caption" color="text.secondary">CV (%)</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 500, color: parseFloat(statisticalSummary.cv) > 10 ? theme.palette.error.main : theme.palette.success.main }}>
                                                    {statisticalSummary.cv}%
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Paper sx={{ p: 1, textAlign: 'center' }}>
                                                <Typography variant="caption" color="text.secondary">Range</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                                    {statisticalSummary.range}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* Set calculated average as result */}
                            {formData.actual_result && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Calculated Average:</strong> {formData.actual_result} {formData.target_unit}
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            )}

            {/* Result Analysis */}
            {Object.keys(resultAnalysis).length > 0 && resultsComplete && (
                <Grid item xs={12}>
                    <Card sx={{ 
                        border: `1px solid ${getResultStatusColor(resultAnalysis.result_status)}`,
                        borderRadius: 3,
                        background: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                {getResultStatusIcon(resultAnalysis.result_status)}
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: getResultStatusColor(resultAnalysis.result_status) }}>
                                        Result Analysis: {resultAnalysis.result_status?.replace('_', ' ')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Comprehensive analysis of test result against specifications
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setShowCalculationDetails(true)}
                                    startIcon={<CalculateIcon />}
                                >
                                    View Details
                                </Button>
                            </Stack>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                                        <Typography variant="caption" color="text.secondary">Actual Result</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 500, color: getResultStatusColor(resultAnalysis.result_status) }}>
                                            {resultAnalysis.actual} {formData.target_unit}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">Deviation from Target</Typography>
                                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                            {resultAnalysis.relativeDeviation > 0 ? 
                                                <TrendingUpIcon sx={{ color: theme.palette.error.main }} /> : 
                                                <TrendingDownIcon sx={{ color: theme.palette.success.main }} />
                                            }
                                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                                {Math.abs(resultAnalysis.relativeDeviation).toFixed(2)}%
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">Within Spec</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: resultAnalysis.withinSpec ? theme.palette.success.main : theme.palette.error.main }}>
                                            {resultAnalysis.withinSpec ? '✓ YES' : '✗ NO'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">Capability Index</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                            {resultAnalysis.capacityIndex || 'N/A'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
            </Grid>
            )}

            {/* Results Summary - Simple */}
            {resultsComplete && (
                <Grid item xs={12}>
                    <Alert 
                        severity="success"
                        sx={{ 
                            borderRadius: 3,
                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '& .MuiAlert-icon': {
                                color: theme.palette.success.main
                            }
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <CheckCircleIcon />
                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                ✓ Test Results Recorded
                            </Typography>
                        </Stack>
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                <strong>Result:</strong> {formData.result_text}
                            </Typography>
                        </Box>
                    </Alert>
                </Grid>
            )}

            {/* Calculation Details Dialog */}
            <Dialog 
                open={showCalculationDetails} 
                onClose={() => setShowCalculationDetails(false)} 
                maxWidth="md" 
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 4,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    }
                }}
            >
                <DialogTitle sx={{ 
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    fontSize: '1.25rem'
                }}>
                    Detailed Result Analysis
                </DialogTitle>
                <DialogContent sx={{ p: 3, backgroundColor: alpha(theme.palette.grey[50], 0.5) }}>
                    {resultAnalysis && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 3, mt: 3, fontWeight: 500, color: theme.palette.text.primary }}>
                                    Calculation Details
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ 
                                    p: 2, 
                                    borderRadius: 3,
                                    border: `1px solid ${theme.palette.grey[400]}`,
                                    backgroundColor: 'white',
                                    backdropFilter: 'blur(10px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                                    },
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 2 }}>
                                        Primary Values
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                        <strong>Actual Value:</strong> {resultAnalysis.actual} {formData.target_unit}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                        <strong>Target Value:</strong> {resultAnalysis.target} {formData.target_unit}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                        <strong>Absolute Deviation:</strong> {resultAnalysis.absoluteDeviation?.toFixed(3)} {formData.target_unit}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        <strong>Relative Deviation:</strong> {resultAnalysis.relativeDeviation?.toFixed(3)}%
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ 
                                    p: 2, 
                                    borderRadius: 3,
                                    border: `1px solid ${theme.palette.grey[400]}`,
                                    backgroundColor: 'white',
                                    backdropFilter: 'blur(10px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                                    },
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 2 }}>
                                        Specification Analysis
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                        <strong>Distance from Min:</strong> {resultAnalysis.distanceFromMin?.toFixed(3)} {formData.target_unit}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                        <strong>Distance from Max:</strong> {resultAnalysis.distanceFromMax?.toFixed(3)} {formData.target_unit}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                        <strong>Within Specification:</strong> 
                                        <Chip 
                                            label={resultAnalysis.withinSpec ? 'YES' : 'NO'}
                                            size="small"
                                            sx={{ 
                                                ml: 1,
                                                fontWeight: 500,
                                                backgroundColor: resultAnalysis.withinSpec ? theme.palette.success.main : theme.palette.error.main,
                                                color: 'white'
                                            }}
                                        />
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        <strong>Cpk Index:</strong> {resultAnalysis.capacityIndex || 'N/A'}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ 
                    p: 3, 
                    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.primary.main, 0.01)})`
                }}>
                    <Button 
                        onClick={() => setShowCalculationDetails(false)}
                        sx={{
                            borderRadius: 3,
                            px: 4,
                            py: 1,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.8)})`,
                            color: 'white',
                            fontWeight: 500,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            backdropFilter: 'blur(10px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.primary.main, 0.7)})`
                            }
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Save Button for New Tests */}
            {!editingTest && resultsComplete && (
                <Grid item xs={12}>
                    <Card sx={{ 
                        borderRadius: 4,
                        border: `1px solid ${theme.palette.success.main}`,
                        background: alpha(theme.palette.success.main, 0.05),
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.success.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        mt: 2
                    }}>
                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.success.main, mb: 2 }}>
                                ✓ Test Complete - Ready to Save
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontWeight: 500 }}>
                                Save this test as pending QA review. A QA manager can approve it later.
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={onSaveTest}
                                sx={{
                                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${alpha(theme.palette.success.main, 0.8)})`,
                                    color: 'white',
                                    fontWeight: 500,
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                    backdropFilter: 'blur(10px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.4)}`,
                                        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.9)}, ${alpha(theme.palette.success.main, 0.7)})`
                                    }
                                }}
                            >
                                Save Test (Pending QA Review)
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            )}
            </Grid>
        </Box>
    );
} 